import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEntity } from '@backstage/plugin-catalog-react';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import {
  ErrorPanel,
  InfoCard,
  Link,
  MissingAnnotationEmptyState,
  Progress,
} from '@backstage/core-components';
import { Box, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

interface OrcaAlertCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
  informational: number;
}

interface OrcaAttackPath {
  id: string;
  riskScore: number;
}

interface OrcaSecuritySummary {
  kubernetesId: string;
  fetchedAt: string;
  alertCounts: OrcaAlertCounts;
  attackPaths: OrcaAttackPath[];
}

const SEVERITY_ROWS: { key: keyof OrcaAlertCounts; label: string }[] = [
  { key: 'critical', label: 'CRITICAL' },
  { key: 'high', label: 'HIGH' },
  { key: 'medium', label: 'MEDIUM' },
  { key: 'low', label: 'LOW' },
  { key: 'informational', label: 'INFORMATIONAL' },
];

export function OrcaAlertsCard(): JSX.Element {
  const theme = useTheme();
  const { entity } = useEntity();
  const navigate = useNavigate();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const annotations = entity.metadata.annotations ?? {};
  const kind = entity.kind.toLowerCase();
  const namespace = entity.metadata.namespace ?? 'default';
  const entityName = entity.metadata.name;
  const selectorMatch = annotations['backstage.io/kubernetes-label-selector']?.match(/github\.repository=([a-zA-Z0-9._\/-]+)/);
  const kubernetesId = (annotations['backstage.io/kubernetes-id'] ?? selectorMatch?.[1])?.toLowerCase();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<OrcaSecuritySummary | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!kubernetesId) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('orca');
        const response = await fetchApi.fetch(
          `${baseUrl}/security-summary?kubernetesId=${encodeURIComponent(kubernetesId)}`,
        );
        if (response.status === 404) {
          setNotFound(true);
          return;
        }
        if (!response.ok) {
          const body = await response.json().catch(() => ({})) as { error?: string };
          throw new Error(body.error ?? `HTTP ${response.status}`);
        }
        setData(await response.json());
      } catch (e) {
        setError(e as Error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [kubernetesId, discoveryApi, fetchApi]);

  if (!kubernetesId) {
    return (
      <InfoCard title="Orca Security">
        <MissingAnnotationEmptyState annotation="backstage.io/kubernetes-id" />
      </InfoCard>
    );
  }

  if (loading) {
    return (
      <InfoCard title="Orca Security">
        <Progress />
      </InfoCard>
    );
  }

  if (notFound) {
    return (
      <InfoCard title="Orca Security">
        <Box p={2} textAlign="center">
          <Typography variant="h6" gutterBottom>
            Orca data is not available yet
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Data will appear after the first polling cycle (~30 seconds after startup).
          </Typography>
        </Box>
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="Orca Security">
        <ErrorPanel error={error} />
      </InfoCard>
    );
  }

  if (!data) return <></>;

  const { alertCounts, fetchedAt, attackPaths = [] } = data;

  const subheader = (
    <Link to={`/catalog/${namespace}/${kind}/${entityName}/orca`}>
      <Typography variant="caption">View details →</Typography>
    </Link>
  );

  const attackPathCounts = { high: 0, medium: 0, low: 0 };
  for (const ap of attackPaths) {
    if (ap.riskScore >= 7) attackPathCounts.high++;
    else if (ap.riskScore >= 4) attackPathCounts.medium++;
    else attackPathCounts.low++;
  }
  const severityColors: Record<string, string> = {
    critical: theme.palette.error.main,
    high: theme.palette.warning.dark,
    medium: theme.palette.warning.light,
    low: theme.palette.info.main,
    informational: theme.palette.text.secondary,
  };

  const attackPathLevels: { key: keyof typeof attackPathCounts; label: string; color: string }[] = [
    { key: 'high',   label: 'HIGH',   color: theme.palette.error.main },
    { key: 'medium', label: 'MEDIUM', color: theme.palette.warning.main },
    { key: 'low',    label: 'LOW',    color: theme.palette.info.main },
  ];

  const formattedDate = new Date(fetchedAt).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const badgeStyle = (color: string, clickable: boolean): React.CSSProperties => ({
    background: color,
    color: 'white',
    borderRadius: 4,
    padding: '3px 10px',
    fontWeight: 700,
    fontSize: 14,
    cursor: clickable ? 'pointer' : 'default',
    border: 'none',
    fontFamily: 'inherit',
  });

  return (
    <InfoCard title="Orca Security" subheader={subheader}>
      <Box p={1}>

        <Box mb={2}>
          <Typography variant="overline" style={{ fontSize: 11, letterSpacing: 1, fontWeight: 600 }}>
            Alerts
          </Typography>
          <Box display="flex" flexWrap="wrap" style={{ gap: 6 }} mt={0.5}>
            {SEVERITY_ROWS.map(({ key, label }) => {
              const count = alertCounts[key];
              const clickable = count > 0;
              return (
                <button
                  key={key}
                  onClick={() => navigate(`/catalog/${namespace}/${kind}/${entityName}/orca?severity=${key}#alerts`)}
                  disabled={!clickable}
                  style={{ ...badgeStyle(severityColors[key], clickable), opacity: clickable ? 1 : 0.5 }}
                >
                  {count} {label}
                </button>
              );
            })}
          </Box>
        </Box>

        <Box mb={2}>
          <Typography variant="overline" style={{ fontSize: 11, letterSpacing: 1, fontWeight: 600 }}>
            Attack Paths
          </Typography>
          <Box display="flex" flexWrap="wrap" style={{ gap: 6 }} mt={0.5}>
            {attackPathLevels.map(({ key, label, color }) => {
              const count = attackPathCounts[key];
              const clickable = count > 0;
              return (
                <button
                  key={key}
                  onClick={() => navigate(`/catalog/${namespace}/${kind}/${entityName}/orca?attackRisk=${key}#attack-paths`)}
                  disabled={!clickable}
                  style={{ ...badgeStyle(color, clickable), opacity: clickable ? 1 : 0.5 }}
                >
                  {count} {label}
                </button>
              );
            })}
          </Box>
        </Box>

        <Typography variant="caption" color="textSecondary">
          Last updated: {formattedDate}
        </Typography>
      </Box>
    </InfoCard>
  );
}
